import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import Autocomplete from "components/Input/Autocomplete";
import { getClientsList } from "services/lists.service";
import {
  PATIENT_SEARCH_PAGE_SIZE,
  searchPatients,
} from "services/patients.service";
import {
  BookingConfig,
  readBookableConfigs,
} from "services/booking-configs.service";
import { ACTIVATION_STATE } from "constants/bookingEnums";

export type BookingSubmission = {
  contactId: number;
  animalId?: number;
  serviceId: number;
  serviceFulfilmentConfigId: number;
  start: string;
  end: string;
};

type Props = {
  open: boolean;
  /** Prefilled from the calendar selection. Local datetime-local strings. */
  initialStart: string;
  initialEnd: string;
  busy?: boolean;
  /**
   * Booking from a patient's own page. The patient is fixed rather than chosen:
   * a dropdown there could book the wrong animal from a screen that names one.
   */
  lockedPatient?: { animalId: number; name: string };
  /** Narrow the client choice, e.g. to the patient's registered owners. */
  clientOptions?: Option[];
  initialContactId?: string;
  onCancel: () => void;
  onSubmit: (values: BookingSubmission) => void;
};

type Option = { title: string; value: string };

const BookingDialog = ({
  open,
  initialStart,
  initialEnd,
  busy,
  lockedPatient,
  clientOptions,
  initialContactId,
  onCancel,
  onSubmit,
}: Props) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();

  const [clients, setClients] = React.useState<Option[]>([]);
  const [configs, setConfigs] = React.useState<BookingConfig[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [contactId, setContactId] = React.useState(initialContactId ?? "");
  const [animalId, setAnimalId] = React.useState(
    lockedPatient ? String(lockedPatient.animalId) : ""
  );
  // The picker searches rather than holding every patient, so the name of the
  // chosen one has to be kept here - there is no list to look it up in.
  const [animalName, setAnimalName] = React.useState(lockedPatient?.name ?? "");
  const [configId, setConfigId] = React.useState("");
  const [start, setStart] = React.useState(initialStart);
  const [end, setEnd] = React.useState(initialEnd);
  const [error, setError] = React.useState<string | null>(null);

  // Reset to the newly selected slot each time the dialog opens, otherwise the
  // previous selection carries over and a booking lands on the wrong day.
  React.useEffect(() => {
    if (!open) return;
    setStart(initialStart);
    setEnd(initialEnd);
    setContactId(initialContactId ?? "");
    setAnimalId(lockedPatient ? String(lockedPatient.animalId) : "");
    setAnimalName(lockedPatient?.name ?? "");
    setConfigId("");
    setError(null);
  }, [open, initialStart, initialEnd, initialContactId, lockedPatient]);

  React.useEffect(() => {
    if (!open || !isAuthenticated || orgId <= 0) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        // Neither list is worth fetching when the caller has already supplied
        // one: the patient page knows its patient and its owners.
        // Patients are not fetched here any more - the picker searches for them
        // as they are typed, so a clinic's whole animal table never gets loaded
        // to fill one dropdown.
        const [clientRes, configRes] = await Promise.all([
          clientOptions ? null : getClientsList(token, orgId),
          readBookableConfigs(token, orgId),
        ]);
        if (cancelled) return;

        if (clientRes) {
          setClients(
            (clientRes.data ?? []).map((c) => ({ title: c.name, value: c.id }))
          );
        }
        // Only active configurations can be booked against.
        setConfigs(
          (configRes.data ?? []).filter(
            (c) => c.isActive === ACTIVATION_STATE.ACTIVE
          )
        );

        const firstError = clientRes?.error || configRes.error;
        if (firstError) setLoadError(firstError.message);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, isAuthenticated, orgId, getAccessTokenSilently, clientOptions, lockedPatient]);

  /**
   * One page of patients matching what has been typed, narrowed to the chosen
   * client. A patient belongs to a client, and offering every animal in the
   * clinic is where the wrong one gets picked.
   */
  const findPatients = React.useCallback(
    async (term: string, page: number) => {
      const token = await getAccessTokenSilently();
      const { data } = await searchPatients(token, {
        orgId,
        term,
        contactId: contactId ? Number(contactId) : undefined,
        pagingStart: page * PATIENT_SEARCH_PAGE_SIZE,
        pagingStop: (page + 1) * PATIENT_SEARCH_PAGE_SIZE,
      });
      return (data ?? []).map((animal) => ({
        title: animal.name,
        value: animal.animalId,
      }));
    },
    [getAccessTokenSilently, orgId, contactId]
  );

  // A patient chosen for one client does not belong to the next.
  React.useEffect(() => {
    if (lockedPatient) return;
    setAnimalId("");
    setAnimalName("");
  }, [contactId, lockedPatient]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactId) {
      setError("Choose a client for this booking.");
      return;
    }
    if (!configId) {
      setError("Choose what is being booked.");
      return;
    }
    if (!start || !end) {
      setError("A booking needs a start and an end.");
      return;
    }
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      setError("The end must come after the start.");
      return;
    }

    const chosen = configs.find(
      (c) => String(c.serviceFulfilmentConfigId) === configId
    );
    if (!chosen) {
      setError("That booking configuration is no longer available.");
      return;
    }

    setError(null);
    onSubmit({
      contactId: Number(contactId),
      animalId: animalId ? Number(animalId) : undefined,
      serviceId: chosen.serviceId,
      serviceFulfilmentConfigId: chosen.serviceFulfilmentConfigId,
      start,
      end,
    });
  };

  const configOptions: Option[] = configs.map((c) => ({
    title: c.name || `Configuration ${c.serviceFulfilmentConfigId}`,
    value: String(c.serviceFulfilmentConfigId),
  }));

  const nothingToBook = !loading && configs.length === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]"
      >
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">New booking</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            {lockedPatient
              ? `Book ${lockedPatient.name} in against one of this clinic's services.`
              : "Book a family in against one of this provider's services."}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          {nothingToBook && (
            <p className="sm:col-span-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              No booking configurations are available - not even the shared
              consultations. That usually means the platform seed has not run.
            </p>
          )}

          <div className="sm:col-span-2">
            <Select
              label="Client"
              placeholder={loading ? "Loading…" : "Choose a client"}
              options={clientOptions ?? clients}
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            {lockedPatient ? (
              <>
                <span className="block text-sm font-medium text-slate-700">
                  Patient
                </span>
                <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {lockedPatient.name}
                </p>
              </>
            ) : (
              <Autocomplete
                label="Deceased (optional)"
                placeholder={
                  contactId
                    ? "Search this family's deceased"
                    : "Search the deceased"
                }
                value={animalId}
                selectedTitle={animalName}
                onChange={(value, option) => {
                  setAnimalId(value);
                  setAnimalName(option?.title ?? "");
                }}
                search={findPatients}
                pageSize={PATIENT_SEARCH_PAGE_SIZE}
                emptyMessage={
                  contactId ? "This family has no matching records" : "No matches"
                }
                scopeKey={contactId}
              />
            )}
          </div>

          <div className="sm:col-span-2">
            <Select
              label="Booking"
              placeholder={loading ? "Loading…" : "Choose what is booked"}
              options={configOptions}
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
            />
          </div>

          <Input
            type="datetime-local"
            label="Start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <Input
            type="datetime-local"
            label="End"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />

          {loadError && (
            <p className="sm:col-span-2 text-sm text-red-500">{loadError}</p>
          )}
          {error && <p className="sm:col-span-2 text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button color="Secondary" onClick={onCancel} disabled={busy}>
            <span>Cancel</span>
          </Button>
          <Button color="Primary" type="submit" disabled={busy || nothingToBook}>
            <span>{busy ? "Booking…" : "Create booking"}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingDialog;
